import { AssetImportError, type AssetMeta, type AssetPutOptions, type AssetStore } from './api';
import { createDbOpener, type AssetRecord } from './db';

const ASSET_DIR = 'assets';

/** Default import ceiling. Browsers decode larger media poorly and OPFS quotas are a share of the disk. */
export const MAX_IMPORT_BYTES = 2 * 1024 ** 3;

/** Bytes hashed per step: bounds memory to one chunk however large the file is. */
const HASH_CHUNK_BYTES = 8 * 1024 * 1024;

type OpfsDir = FileSystemDirectoryHandle;

/** OPFS is usable only if the root is reachable and files can be written from the main thread. */
async function openOpfsAssetDir(): Promise<OpfsDir | null> {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.storage?.getDirectory !== 'function') return null;
    if (typeof FileSystemFileHandle === 'undefined' || !('createWritable' in FileSystemFileHandle.prototype)) return null;
    const root = await navigator.storage.getDirectory();
    return await root.getDirectoryHandle(ASSET_DIR, { create: true });
  } catch {
    return null;
  }
}

/** Asks the browser not to evict our data. Resolves to the browser's real answer. */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
    return (await navigator.storage.persisted?.()) || (await navigator.storage.persist());
  } catch {
    return false;
  }
}

function hex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');
}

function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(n % 1024 ** 3 ? 1 : 0)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(n % 1024 ** 2 ? 1 : 0)} MB`;
  return `${Math.ceil(n / 1024)} KB`;
}

function abortError(): DOMException {
  return new DOMException('The import was cancelled', 'AbortError');
}

/**
 * Content key for de-duplication, read chunk by chunk so a multi-gigabyte file
 * is never held in memory. A file that fits in one chunk keys on its plain
 * SHA-256 (as stores written before chunking did); a larger one on 'c:' plus
 * the SHA-256 of its chunks' SHA-256 digests, since WebCrypto cannot digest
 * incrementally.
 */
async function contentHash(blob: Blob, signal?: AbortSignal, onProgress?: (fraction: number) => void): Promise<string> {
  onProgress?.(0);
  if (blob.size <= HASH_CHUNK_BYTES) {
    const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    if (signal?.aborted) throw abortError();
    onProgress?.(1);
    return hex(digest);
  }
  const chunkCount = Math.ceil(blob.size / HASH_CHUNK_BYTES);
  const digests = new Uint8Array(chunkCount * 32);
  for (let i = 0; i < chunkCount; i++) {
    if (signal?.aborted) throw abortError();
    const chunk = await blob.slice(i * HASH_CHUNK_BYTES, (i + 1) * HASH_CHUNK_BYTES).arrayBuffer();
    digests.set(new Uint8Array(await crypto.subtle.digest('SHA-256', chunk)), i * 32);
    onProgress?.((i + 1) / chunkCount);
  }
  if (signal?.aborted) throw abortError();
  return `c:${hex(await crypto.subtle.digest('SHA-256', digests))}`;
}

const EXTENSION_TYPES: Record<string, string> = {
  png: 'image/png',
  apng: 'image/apng',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  flac: 'audio/flac',
  ttf: 'font/ttf',
  otf: 'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2',
};

/**
 * MIME type from a file's leading bytes (at least 512 for SVG), or null when
 * the signature is not one of the image, audio, video or font formats the
 * studio uses.
 */
export function sniffMimeType(bytes: Uint8Array): string | null {
  const ascii = (from: number, to: number) => (bytes.length >= to ? String.fromCharCode(...bytes.subarray(from, to)) : '');
  const at = (offset: number, ...values: number[]) => values.every((v, i) => bytes[offset + i] === v);
  if (at(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png';
  if (at(0, 0xff, 0xd8, 0xff)) return 'image/jpeg';
  if (ascii(0, 4) === 'GIF8') return 'image/gif';
  if (ascii(0, 2) === 'BM' && bytes.length >= 14) return 'image/bmp';
  if (ascii(0, 4) === 'RIFF') {
    const form = ascii(8, 12);
    if (form === 'WEBP') return 'image/webp';
    if (form === 'WAVE') return 'audio/wav';
    if (form === 'AVI ') return 'video/x-msvideo';
  }
  if (ascii(4, 8) === 'ftyp') {
    const brand = ascii(8, 12);
    if (brand === 'avif' || brand === 'avis') return 'image/avif';
    if (brand === 'qt  ') return 'video/quicktime';
    if (brand === 'M4A ' || brand === 'M4B ') return 'audio/mp4';
    return 'video/mp4';
  }
  if (at(0, 0x1a, 0x45, 0xdf, 0xa3)) {
    // The EBML header names the doctype within its first few dozen bytes.
    return ascii(0, Math.min(bytes.length, 64)).includes('webm') ? 'video/webm' : 'video/x-matroska';
  }
  if (ascii(0, 4) === 'OggS') return 'audio/ogg';
  if (ascii(0, 4) === 'fLaC') return 'audio/flac';
  if (ascii(0, 3) === 'ID3') return 'audio/mpeg';
  // Frame sync: 11 set bits, then the layer field is 00 for ADTS AAC and non-zero for MPEG audio.
  if (bytes[0] === 0xff && (bytes[1] & 0xf6) === 0xf0) return 'audio/aac';
  if (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0 && (bytes[1] & 0x06) !== 0) return 'audio/mpeg';
  if (ascii(0, 4) === 'wOF2') return 'font/woff2';
  if (ascii(0, 4) === 'wOFF') return 'font/woff';
  if (ascii(0, 4) === 'OTTO') return 'font/otf';
  if (at(0, 0x00, 0x01, 0x00, 0x00) || ascii(0, 4) === 'true') return 'font/ttf';
  const text = new TextDecoder().decode(bytes.subarray(0, 512)).replace(/^\uFEFF/, '').trimStart();
  if (/^(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*(<!DOCTYPE svg[^>]*>\s*)?<svg[\s>]/i.test(text)) return 'image/svg+xml';
  return null;
}

/** The type to store: the declared one, else sniffed from the bytes, else from the name's extension. */
async function resolveMimeType(file: Blob, name: string): Promise<string> {
  const declared = file.type.toLowerCase();
  if (declared && declared !== 'application/octet-stream') return file.type;
  const sniffed = sniffMimeType(new Uint8Array(await file.slice(0, 512).arrayBuffer()));
  if (sniffed) return sniffed;
  const extension = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
  return EXTENSION_TYPES[extension] ?? 'application/octet-stream';
}

function isQuotaError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22);
}

function storageFull(name: string): AssetImportError {
  return new AssetImportError('storage-full', `There is not enough browser storage left for "${name}". Delete unused assets or recordings and try again.`);
}

/** Refuses up front when the browser reports less free quota than the file needs. */
async function checkQuota(size: number, name: string): Promise<void> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.quota !== undefined && estimate.usage !== undefined && estimate.quota - estimate.usage < size) throw storageFull(name);
  } catch (err) {
    if (err instanceof AssetImportError) throw err;
  }
}

function newAssetId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
}

function toMeta(rec: AssetRecord): AssetMeta {
  return { id: rec.id, name: rec.name, mimeType: rec.mimeType, sizeBytes: rec.sizeBytes, createdAt: rec.createdAt };
}

export function createAssetStore(): AssetStore {
  const open = createDbOpener();
  const urls = new Map<string, Promise<string | null>>();
  // Chosen once: both backends are real, the choice depends only on what the browser offers.
  const opfs = openOpfsAssetDir();
  void requestPersistentStorage();
  // Serialises writers so two puts of identical content cannot both pass the de-duplication check.
  let writeChain: Promise<unknown> = Promise.resolve();

  const serial = <T>(fn: () => Promise<T>): Promise<T> => {
    const run = writeChain.then(fn, fn);
    writeChain = run.catch(() => undefined);
    return run;
  };

  const ready = new Map<string, string>();

  const revoke = (id: string): void => {
    const pending = urls.get(id);
    urls.delete(id);
    const url = ready.get(id);
    if (url) {
      ready.delete(id);
      URL.revokeObjectURL(url);
    } else {
      // Still resolving: revoke as soon as the URL exists.
      void pending?.then((late) => late && URL.revokeObjectURL(late), () => undefined);
    }
  };

  async function readBlob(rec: AssetRecord): Promise<Blob | null> {
    if (rec.storage === 'idb') {
      const db = await open();
      const blob = await db.get('assetBlobs', rec.id);
      return blob ?? null;
    }
    const dir = await opfs;
    if (!dir) return null;
    try {
      const file = await (await dir.getFileHandle(rec.id)).getFile();
      // Wrapping keeps the bytes file-backed but restores the recorded type.
      return new Blob([file], { type: rec.mimeType });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotFoundError') return null;
      throw err;
    }
  }

  async function removeBytes(rec: AssetRecord): Promise<void> {
    if (rec.storage === 'idb') {
      const db = await open();
      await db.delete('assetBlobs', rec.id);
      return;
    }
    const dir = await opfs;
    try {
      await dir?.removeEntry(rec.id);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'NotFoundError')) throw err;
    }
  }

  return {
    put(file, name, options: AssetPutOptions = {}) {
      const { signal, onProgress } = options;
      const maxBytes = options.maxBytes ?? MAX_IMPORT_BYTES;
      if (file.size > maxBytes) {
        return Promise.reject(
          new AssetImportError('too-large', `"${name}" is ${formatBytes(file.size)}; the limit is ${formatBytes(maxBytes)}.`),
        );
      }
      if (signal?.aborted) return Promise.reject(abortError());
      return serial(async () => {
        if (signal?.aborted) throw abortError();
        const db = await open();
        const hash = await contentHash(file, signal, onProgress);
        const existing = await db.getFromIndex('assets', 'byHash', hash);
        if (existing) return toMeta(existing);

        const mimeType = await resolveMimeType(file, name);
        const typed = file.type === mimeType ? file : new Blob([file], { type: mimeType });
        await checkQuota(file.size, name);
        if (signal?.aborted) throw abortError();

        const dir = await opfs;
        const record: AssetRecord = {
          id: newAssetId(),
          name,
          mimeType,
          sizeBytes: file.size,
          createdAt: Date.now(),
          hash,
          storage: dir ? 'opfs' : 'idb',
        };

        if (dir) {
          const handle = await dir.getFileHandle(record.id, { create: true });
          const writable = await handle.createWritable();
          try {
            await writable.write(typed);
            if (signal?.aborted) throw abortError();
            await writable.close();
          } catch (err) {
            await writable.abort().catch(() => undefined);
            await dir.removeEntry(record.id).catch(() => undefined);
            throw isQuotaError(err) ? storageFull(name) : err;
          }
          try {
            await db.put('assets', record);
          } catch (err) {
            await dir.removeEntry(record.id).catch(() => undefined);
            throw isQuotaError(err) ? storageFull(name) : err;
          }
        } else {
          try {
            const tx = db.transaction(['assets', 'assetBlobs'], 'readwrite');
            void tx.objectStore('assetBlobs').put(typed, record.id);
            void tx.objectStore('assets').put(record);
            await tx.done;
          } catch (err) {
            throw isQuotaError(err) ? storageFull(name) : err;
          }
        }
        return toMeta(record);
      });
    },

    async getMeta(id) {
      const db = await open();
      const rec = await db.get('assets', id);
      return rec ? toMeta(rec) : null;
    },

    async list() {
      const db = await open();
      const all = await db.getAll('assets');
      return all.map(toMeta).sort((a, b) => b.createdAt - a.createdAt);
    },

    async getBlob(id) {
      const db = await open();
      const rec = await db.get('assets', id);
      return rec ? readBlob(rec) : null;
    },

    getUrl(id) {
      let pending = urls.get(id);
      if (!pending) {
        pending = (async () => {
          const db = await open();
          const rec = await db.get('assets', id);
          const blob = rec ? await readBlob(rec) : null;
          return blob ? URL.createObjectURL(blob) : null;
        })();
        urls.set(id, pending);
        // Do not cache failures or misses: the asset may be added later.
        void pending.then(
          (url) => {
            if (urls.get(id) !== pending) return;
            if (url === null) urls.delete(id);
            else ready.set(id, url);
          },
          () => {
            if (urls.get(id) === pending) urls.delete(id);
          },
        );
      }
      return pending;
    },

    delete(id) {
      return serial(async () => {
        revoke(id);
        const db = await open();
        const rec = await db.get('assets', id);
        if (!rec) return;
        await db.delete('assets', id);
        await removeBytes(rec);
      });
    },

    dispose() {
      for (const id of [...urls.keys()]) revoke(id);
    },
  };
}
